import { computed, DestroyRef, DOCUMENT, inject, Injectable, signal } from '@angular/core';
import { NotificationsService } from '@shared/utils';

/**
 * Pevná šířka obsahu max 1440 px na střed (gov.cz). false = celá šířka okna jako v původní aplikaci (rozhodnutí
 * zákazníka); CSS třída layout-fixed-width (styles/reza-components.css, oddíl 8).
 */
export const CONTENT_FIXED_WIDTH = false;

/**
 * Easter egg: skrytý panel „Šířka obsahu“ pro vyzkoušení šířky layoutu (pevná 1440 px, při ní přehledy nebo detaily
 * přes celou šířku). Použití: 3× do 3 s kliknout na „Verze: …“ v patičce, v záhlaví se objeví tlačítko s panelem (od
 * šířky okna 1024 px); tlačítko „Skrýt a obnovit výchozí“ v panelu ho zase schová. Platí jen v daném prohlížeči.
 * 'skryty' = easter egg, 'zapnuty' = panel vždy vidět, 'vypnuty' = úplně vypnutý (uložené volby se neuplatní).
 */
export const CONTENT_WIDTH_PANEL: 'skryty' | 'zapnuty' | 'vypnuty' = 'skryty';

/** Volby panelu; klíč je zároveň klíčem v localStorage. */
const OPTION_KEYS = ['fixedWidth', 'fullWidthBrowse', 'fullWidthDetail', 'filterCapped'] as const;
export type ContentWidthOption = (typeof OPTION_KEYS)[number];

/** Typ stránky podle obsahu; přes celou šířku může jít jen přehled a detail. */
export type ContentPageType = 'prehled' | 'detail' | 'jina';

/**
 * Prvky, podle kterých se pozná typ stránky. Stejné selektory používá CSS (styles/reza-components.css, oddíl 8);
 * detail s vnořeným přehledem (záložka s tabulkou) je detail.
 */
const DETAIL = 'reza-ui-detail-heading';
const PREHLED = '.reza-overview, .reza-ciselnik-overview';

/** Klíč v localStorage pro odemknutý panel. */
const PANEL_KEY = 'contentWidthPanel';
/** Odemknutí: počet kliknutí na verzi v časovém okně. */
const UNLOCK_CLICKS = 3;
const UNLOCK_WINDOW_MS = 3_000;

/** Šířka obsahu: výchozí podle CONTENT_FIXED_WIDTH, volby panelu jen při odemknutém panelu (CONTENT_WIDTH_PANEL). */
@Injectable({ providedIn: 'root' })
export class ContentWidthService {
  private readonly document = inject(DOCUMENT);
  private readonly notifications = inject(NotificationsService);
  private clicks = 0;
  private clicksStart = 0;

  /** Panel je v záhlaví vidět. */
  readonly panelVisible = signal(
    CONTENT_WIDTH_PANEL === 'zapnuty' || (CONTENT_WIDTH_PANEL === 'skryty' && this.load(PANEL_KEY) === 'true')
  );

  readonly options = {
    fixedWidth: signal(this.readOption('fixedWidth')),
    fullWidthBrowse: signal(this.readOption('fullWidthBrowse')),
    fullWidthDetail: signal(this.readOption('fullWidthDetail')),
    filterCapped: signal(this.readOption('filterCapped')),
  };

  /** Je zapnutá aspoň jedna volba odlišná od výchozího stavu (zvýraznění tlačítka v záhlaví). */
  readonly isActive = computed(() => OPTION_KEYS.some((option) => this.options[option]() !== this.defaultOf(option)));

  constructor() {
    this.apply();
    if (CONTENT_WIDTH_PANEL === 'vypnuty') return;
    // změna v jiném tabu téže aplikace
    const onStorage = ({ key, newValue }: StorageEvent) => {
      if (key === PANEL_KEY) {
        this.panelVisible.set(CONTENT_WIDTH_PANEL === 'zapnuty' || newValue === 'true');
      } else if (OPTION_KEYS.includes(key as ContentWidthOption)) {
        const option = key as ContentWidthOption;
        this.options[option].set(newValue === null ? this.defaultOf(option) : newValue === 'true');
      } else return;
      this.apply();
    };
    const window = this.document.defaultView;
    window?.addEventListener('storage', onStorage);
    inject(DestroyRef).onDestroy(() => window?.removeEventListener('storage', onStorage));
  }

  /** Typ právě zobrazené stránky (panel ukazuje, čeho se přepínače týkají). */
  pageType(): ContentPageType {
    if (this.document.querySelector(DETAIL)) return 'detail';
    return this.document.querySelector(PREHLED) ? 'prehled' : 'jina';
  }

  /** Přehledy a detaily přes celou šířku mají smysl jen při pevné šířce, filtr jako při 1440 px jen u přehledů. */
  isDisabled(option: ContentWidthOption): boolean {
    if (option === 'fixedWidth') return false;
    return !this.options.fixedWidth() || (option === 'filterCapped' && !this.options.fullWidthBrowse());
  }

  set(option: ContentWidthOption, value: boolean): void {
    this.options[option].set(value);
    this.apply();
    this.store(option, String(value));
  }

  /** Kliknutí na verzi v patičce; 3 kliknutí do 3 s odemknou panel. */
  versionClick(): void {
    if (CONTENT_WIDTH_PANEL !== 'skryty' || this.panelVisible()) return;
    const now = Date.now();
    if (now - this.clicksStart > UNLOCK_WINDOW_MS) {
      this.clicksStart = now;
      this.clicks = 0;
    }
    if (++this.clicks < UNLOCK_CLICKS) return;
    this.panelVisible.set(true);
    this.store(PANEL_KEY, 'true');
    this.apply();
    this.notifications.dispatchInfo('Ovládání šířky obsahu je v záhlaví (od šířky okna 1024 px).');
  }

  /** Skryje panel a vrátí všechny volby na výchozí stav. */
  hide(): void {
    if (CONTENT_WIDTH_PANEL !== 'skryty') return;
    this.panelVisible.set(false);
    for (const option of OPTION_KEYS) {
      this.options[option].set(this.defaultOf(option));
      this.store(option, null);
    }
    this.store(PANEL_KEY, null);
    this.apply();
  }

  private defaultOf(option: ContentWidthOption): boolean {
    return option === 'fixedWidth' && CONTENT_FIXED_WIDTH;
  }

  /** Uložená volba; při skrytém panelu výchozí stav. */
  private readOption(option: ContentWidthOption): boolean {
    const value = this.panelVisible() ? this.load(option) : null;
    return value === null ? this.defaultOf(option) : value === 'true';
  }

  private load(key: string): string | null {
    try {
      return this.document.defaultView?.localStorage.getItem(key) ?? null;
    } catch {
      return null;
    }
  }

  /** Uloží hodnotu do localStorage (null = smaže); bez localStorage (anonymní okno) platí jen do obnovení stránky. */
  private store(key: string, value: string | null): void {
    try {
      const storage = this.document.defaultView?.localStorage;
      if (value === null) storage?.removeItem(key);
      else storage?.setItem(key, value);
    } catch {
      // localStorage nemusí být dostupný
    }
  }

  /** Třídy na <html>; kterých stránek se týkají, rozhoduje CSS (styles/reza-components.css, oddíl 8). */
  private apply(): void {
    const on = this.panelVisible();
    const active = (option: ContentWidthOption) => on && this.options[option]() && !this.isDisabled(option);
    const { classList } = this.document.documentElement;
    classList.toggle('layout-fixed-width', on ? this.options.fixedWidth() : CONTENT_FIXED_WIDTH);
    classList.toggle('layout-full-browse', active('fullWidthBrowse'));
    classList.toggle('layout-full-detail', active('fullWidthDetail'));
    classList.toggle('layout-filter-capped', active('filterCapped'));
  }
}
